import React from 'react';
import { Link } from 'react-router-dom';
import {
  FiFileText,
  FiDollarSign,
  FiClipboard,
  FiUsers,
  FiCalendar,
  FiShield,
  FiGlobe,
  FiCheck,
  FiArrowRight,
} from 'react-icons/fi';
import './HomePage.css';

const HomePage: React.FC = () => {
  return (
    <div className="home-page">
      {/* Hero Section */}
      <section className="hero">
        <div className="hero-content container">
          <h1>
            Manage Documents. <br />
            Simplify Bookkeeping. <br />
            <span className="hero-highlight">File Taxes with Confidence.</span>
          </h1>
          <p className="hero-subtitle">
            The all-in-one platform for businesses and professionals.
            Document management, bookkeeping, and tax filing — unified,
            secure, and available across cities, states, and countries.
          </p>
          <div className="hero-cta">
            <Link to="/auth" className="btn btn-primary btn-lg">
              Get Started Free
              <FiArrowRight />
            </Link>
            <Link to="/pricing" className="btn btn-outline btn-lg">
              View Pricing
            </Link>
          </div>
          <p className="hero-note">No credit card required. Free 14-day trial.</p>
        </div>
      </section>

      {/* Services Section */}
      <section className="services" id="services">
        <div className="container">
          <h2 className="section-title">Everything You Need in One Place</h2>
          <p className="section-subtitle">
            Powerful tools designed for businesses of every size
          </p>
          <div className="services-grid">
            <div className="service-card">
              <div className="service-icon"><FiFileText /></div>
              <h3>Document Management</h3>
              <p>
                Organize, store, and retrieve documents effortlessly.
                Secure cloud storage with version control, tagging,
                and powerful search.
              </p>
              <Link to="/services/dms" className="service-link">
                Learn more <FiArrowRight />
              </Link>
            </div>
            <div className="service-card">
              <div className="service-icon icon-green"><FiDollarSign /></div>
              <h3>Bookkeeping</h3>
              <p>
                Automated bookkeeping that saves you hours every week.
                Track income, expenses, invoices, and generate financial
                reports instantly.
              </p>
              <Link to="/services/bookkeeping" className="service-link">
                Learn more <FiArrowRight />
              </Link>
            </div>
            <div className="service-card">
              <div className="service-icon icon-orange"><FiClipboard /></div>
              <h3>Tax Filing</h3>
              <p>
                Multi-jurisdiction tax filing made simple. Stay compliant
                across cities, states, and countries with automated
                calculations and e-filing.
              </p>
              <Link to="/services/tax-filing" className="service-link">
                Learn more <FiArrowRight />
              </Link>
            </div>
            <div className="service-card">
              <div className="service-icon icon-purple"><FiUsers /></div>
              <h3>Contact Management</h3>
              <p>
                Keep all your business contacts organized. Manage clients,
                vendors, partners with detailed profiles and interaction
                history.
              </p>
              <Link to="/services/contacts" className="service-link">
                Learn more <FiArrowRight />
              </Link>
            </div>
            <div className="service-card">
              <div className="service-icon icon-teal"><FiCalendar /></div>
              <h3>Calendar Management</h3>
              <p>
                Schedule deadlines, meetings, and filing dates. Never miss
                a tax deadline or client appointment again with smart
                reminders.
              </p>
              <Link to="/services/calendar" className="service-link">
                Learn more <FiArrowRight />
              </Link>
            </div>
            <div className="service-card">
              <div className="service-icon icon-red"><FiShield /></div>
              <h3>Security & Compliance</h3>
              <p>
                Bank-level encryption, role-based access control, and
                audit trails. Stay compliant with industry regulations
                across jurisdictions.
              </p>
              <Link to="/security" className="service-link">
                Learn more <FiArrowRight />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="why-us">
        <div className="container">
          <h2 className="section-title">Why Businesses Choose Akountant</h2>
          <div className="why-grid">
            <div className="why-item">
              <FiGlobe className="why-icon" />
              <h3>Multi-Region Support</h3>
              <p>Operate across cities, states, and countries with localized tax rules, currencies, and compliance standards.</p>
            </div>
            <div className="why-item">
              <FiUsers className="why-icon" />
              <h3>Team Collaboration</h3>
              <p>Master accounts with sub-users. Assign roles — accountant, bookkeeper, editor, admin — with granular permissions.</p>
            </div>
            <div className="why-item">
              <FiShield className="why-icon" />
              <h3>Enterprise Security</h3>
              <p>End-to-end encryption, two-factor authentication, and comprehensive audit logging for complete peace of mind.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Preview */}
      <section className="pricing-preview">
        <div className="container">
          <h2 className="section-title">Simple, Transparent Pricing</h2>
          <p className="section-subtitle">Choose the plan that fits your business</p>
          <div className="pricing-grid">
            <div className="pricing-card">
              <h3>Starter</h3>
              <div className="price">$29<span>/month</span></div>
              <ul>
                <li><FiCheck /> Document Management</li>
                <li><FiCheck /> Basic Bookkeeping</li>
                <li><FiCheck /> 5 Users</li>
                <li><FiCheck /> 10GB Storage</li>
              </ul>
              <Link to="/auth" className="btn btn-outline">Start Free Trial</Link>
            </div>
            <div className="pricing-card featured">
              <div className="popular-badge">Most Popular</div>
              <h3>Professional</h3>
              <div className="price">$79<span>/month</span></div>
              <ul>
                <li><FiCheck /> Everything in Starter</li>
                <li><FiCheck /> Tax Filing</li>
                <li><FiCheck /> 25 Users</li>
                <li><FiCheck /> 100GB Storage</li>
                <li><FiCheck /> Priority Support</li>
              </ul>
              <Link to="/auth" className="btn btn-primary">Start Free Trial</Link>
            </div>
            <div className="pricing-card">
              <h3>Enterprise</h3>
              <div className="price">Custom</div>
              <ul>
                <li><FiCheck /> Everything in Professional</li>
                <li><FiCheck /> Unlimited Users</li>
                <li><FiCheck /> Unlimited Storage</li>
                <li><FiCheck /> Dedicated Support</li>
                <li><FiCheck /> Custom Integrations</li>
              </ul>
              <Link to="/contact" className="btn btn-outline">Contact Sales</Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="container">
          <h2>Ready to Streamline Your Business?</h2>
          <p>Join thousands of businesses that trust Akountant for their document management, bookkeeping, and tax filing needs.</p>
          <Link to="/auth" className="btn btn-primary btn-lg">
            Create Your Free Account
            <FiArrowRight />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
